/* eslint-disable max-lines-per-function, default-param-last, sonarjs/cognitive-complexity */
import { parseNumberType, formatVariableText } from 'src/lib/util/helpers';
import { getNumbersCalculation } from 'src/processes/CMS/processes/management/utils/rules/get_numbers_calcaulation';
import { arithmeticTypes, types } from 'src/processes/CMS/processes/management/pages/BuilderPage/Condition/Actions/actionConstants';

const isVariablesSame = (oldVariables, newVariables) => {
  if (oldVariables.length !== newVariables.length) {
    return false;
  }

  for (const oldVariable of oldVariables) {
    let found = false;
    for (const [j, newVariable] of newVariables.entries()) {
      if (oldVariable.id === newVariable.id && oldVariable.value === newVariables[j].value) {
        found = true;
        break;
      }
    }
    if (!found) {
      return false;
    }
  }

  return true;
};

const getVariableCalculationValue = variable => {
  if (!variable) {
    return { amount: 0, valueType: 'digit' };
  }

  if (variable.type === 'Number') {
    return {
      amount: parseNumberType(variable.value ?? variable.defaultValue ?? 0),
      valueType: 'digit',
    };
  }

  return {
    amount: variable.value ?? variable.defaultValue ?? '',
    valueType: 'string',
  };
};

export const getResultVariableValue = (expression = [], variables = [], fallbackValue) => {
  if (expression.length === 0) {
    return fallbackValue;
  }

  const calculationBlocks = expression.reduce((accumulator, block) => {
    const arithmeticOperator = block?.arithmeticOperator ?? (block.type === types.arithmeticOperator ? block.value : null);

    if (arithmeticOperator && Object.values(arithmeticTypes).includes(arithmeticOperator)) {
      accumulator.push(arithmeticOperator);
      return accumulator;
    }

    if (block.type === types.value || block.type === types.element) {
      accumulator.push(block.value);
      return accumulator;
    }

    if (block.type === types.variable) {
      const variableId = block.originalValue ?? block.value;
      const currentVariable = variables.find(variable => variable.id === variableId);
      accumulator.push(getVariableCalculationValue(currentVariable));
    }

    return accumulator;
  }, []);

  return calculationBlocks.length > 0 ? getNumbersCalculation(calculationBlocks) : fallbackValue;
};

export const getUpdatedVariablesFromResults = (customVariables = [], resultVariables = []) => {
  const defaultVariables = customVariables.map(variable => ({
    ...variable,
    value: variable.defaultValue,
  }));

  if (defaultVariables.length === 0 || !resultVariables) {
    return defaultVariables;
  }

  let updatedVariables = [...defaultVariables];

  for (const resultBlock of resultVariables) {
    const { conditionValue, value, expression, resultOperator, resultVariables: { element } = {} } = resultBlock;
    const currentVariable = updatedVariables.find(variableObj => variableObj.id === element);

    if (conditionValue && currentVariable) {
      const resolvedValue = getResultVariableValue(expression, updatedVariables, value);
      const isNumber = currentVariable.type === 'Number';

      let currentValue = isNumber ? parseNumberType(currentVariable.value) : currentVariable.value;
      const addedAmount = isNumber ? parseNumberType(resolvedValue?.amount) : resolvedValue?.amount;

      switch (resultOperator) {
        case '=': {
          currentValue = addedAmount;
          break;
        }
        case '+=': {
          currentValue += addedAmount;
          break;
        }
        case '-=': {
          if (isNumber) {
            currentValue -= addedAmount;
          }
          break;
        }
        default:
        // do nothing
      }

      updatedVariables = updatedVariables.map(variableObj => {
        return {
          ...variableObj,
          value: variableObj.id === currentVariable.id ? currentValue : variableObj.value,
        };
      });
    }
  }

  return updatedVariables;
};

export const useVariables = (pagesFlow = {}, setPagesFlow) => {
  const { finder = {} } = pagesFlow;

  const { customVariables = [] } = finder;

  const variables = customVariables.reduce((variablesObject, currentItem) => {
    const newVariablesObject = { ...variablesObject };
    newVariablesObject[currentItem.name] = currentItem;

    return newVariablesObject;
  }, {});

  const applyVariable = valueToReplace => {
    if (valueToReplace === undefined) {
      return valueToReplace;
    }

    const stringToReplace = String(valueToReplace);
    if (!stringToReplace) {
      return stringToReplace;
    }

    const regex = /\$\w+\$/g;

    return stringToReplace.replace(regex, match => {
      const keyWithoutSign = match.replaceAll('$', '');
      const matchedVariable = variables[keyWithoutSign];

      const variableValue = matchedVariable?.value ?? matchedVariable?.defaultValue ?? '';
      return formatVariableText(variableValue, matchedVariable?.type === 'Number');
    });
  };

  const applyVariablesToObject = dataObject => {
    return Object.keys(dataObject).reduce((acc, key) => {
      if (dataObject[key] && dataObject[key].getTime) {
        acc[key] = dataObject[key];
      } else if (typeof dataObject[key] === 'string') {
        acc[key] = applyVariable(dataObject[key]);
      } else if (Array.isArray(dataObject[key])) {
        acc[key] = dataObject[key].map(item => (typeof item === 'string' ? applyVariable(item) : applyVariablesToObject(item)));
      } else if (typeof dataObject[key] === 'object' && dataObject[key] !== null) {
        acc[key] = applyVariablesToObject(dataObject[key]);
      } else {
        acc[key] = dataObject[key];
      }
      return acc;
    }, {});
  };

  const setVariable = (id, value) => {
    const updatedVariables = customVariables.map(variable => {
      return {
        ...variable,
        value: variable.id === id ? value : variable.value,
      };
    });

    setPagesFlow(prevPagesFlow => ({
      ...prevPagesFlow,
      finder: {
        ...prevPagesFlow.finder,
        customVariables: updatedVariables,
      },
    }));
  };

  const getVariable = id => {
    return customVariables.find(variable => variable.id === id);
  };

  const getDefaultVariables = () => {
    return customVariables.map(variable => {
      return {
        ...variable,
        value: variable.defaultValue,
      };
    });
  };

  const processVariables = resultVariables => {
    if (!resultVariables) {
      return;
    }

    if (!getDefaultVariables()?.length) {
      return;
    }

    const updatedVariables = getUpdatedVariablesFromResults(customVariables, resultVariables);

    if (!isVariablesSame(customVariables, updatedVariables)) {
      setPagesFlow(prevPagesFlow => ({
        ...prevPagesFlow,
        finder: {
          ...prevPagesFlow.finder,
          customVariables: updatedVariables,
        },
      }));
    }
  };

  return {
    applyVariable,
    applyVariablesToObject,
    getVariable,
    setVariable,
    processVariables,
  };
};
